import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";

import fonts from "@/design/typography";
import colors from "@/design/colors";
import ModalShell from "@/components/common/ModalShell";
import IconRightArrow from "@/components/icons/landing_page/mian/rightArrow";
import { Page_settings } from "@/config/page_settings";
import Info from "@/components/icons/info";

/* ----------------------------------
 * TYPES
 ---------------------------------- */
type Props = {
  onClose: () => void;

  // 동일 금액 작성하기 클릭 시
  onClickSame: () => void;

  // 개별 금액 작성하기 클릭 시
  onClickIndividual: () => void;

  // 이미지 경로는 프로젝트마다 달라서 페이지에서 주입
  sameImageSrc: string;
  individualImageSrc: string;
  title?: string;
};

const BillTemplateCreateTypeModal: React.FC<Props> = ({
  onClose,
  onClickSame,
  onClickIndividual,
  sameImageSrc,
  individualImageSrc,
  title = "청구 템플릿 작성 기준을 선택해 주세요",
}) => {
  const context: any = useContext(Page_settings);
  const [showBusinessWarning, setShowBusinessWarning] = useState(false);
  const [isBusinessRegistered, setIsBusinessRegistered] = useState(false);

  useEffect(() => {
    context.get(
      "/business-registration/me",
      {},
      (res: any) => {
        const status = res?.status ?? "NONE";
        const isRegistered = status !== "NONE";
        setIsBusinessRegistered(isRegistered);
        setShowBusinessWarning(!isRegistered);
      },
      (error: string) => {
        console.error("사업장 등록 여부 조회 실패:", error);
        setIsBusinessRegistered(false);
        setShowBusinessWarning(true);
      },
    );
  }, []);

  const isCardDisabled = !isBusinessRegistered;

  return (
    <ModalShell
      isOpen
      onClose={onClose}
      title={title}
      titleAlign="center"
      width="550px"
    >
      {showBusinessWarning && (
        <WarningBox role="alert">
          <Info color={colors.warning100} width="22px" height="22px" />
          <WarningText>
            템플릿 추가 기능은 사업장 등록 이후 사용 가능합니다
          </WarningText>
        </WarningBox>
      )}

      <Content>
        {/* 동일 금액 */}
        <OptionPanel
          onClick={isCardDisabled ? undefined : onClickSame}
          $disabled={isCardDisabled}
          aria-disabled={isCardDisabled}
        >
          <WhiteCard $disabled={isCardDisabled}>
            <PreviewFrame style={{ padding: "0px 18px" }}>
              <PreviewImage src={sameImageSrc} alt="same_amount_preview" />
            </PreviewFrame>

            <CardBottom>
              <TextArea>
                <CardTitle>동일 금액으로 작성하기</CardTitle>
                <CardSubTitle>
                  여러 수신인에게 같은 금액을 청구할 때
                </CardSubTitle>
              </TextArea>

              <CircleButton
                type="button"
                aria-label="go_same"
                disabled={isCardDisabled}
                $disabled={isCardDisabled}
              >
                <IconRightArrow
                  width="20px"
                  height="20px"
                  color={colors.white}
                />
              </CircleButton>
            </CardBottom>
          </WhiteCard>
        </OptionPanel>

        {/* 개별 금액 */}
        <OptionPanel
          onClick={isCardDisabled ? undefined : onClickIndividual}
          $disabled={isCardDisabled}
          aria-disabled={isCardDisabled}
        >
          <WhiteCard $disabled={isCardDisabled}>
            <PreviewFrame>
              <PreviewImage
                src={individualImageSrc}
                alt="individual_amount_preview"
              />
            </PreviewFrame>

            <CardBottom>
              <TextArea>
                <CardTitle>개별 금액으로 작성하기</CardTitle>
                <CardSubTitle>수신인마다 다른 금액을 청구할 때</CardSubTitle>
              </TextArea>

              <CircleButton
                type="button"
                aria-label="go_individual"
                disabled={isCardDisabled}
                $disabled={isCardDisabled}
              >
                <IconRightArrow
                  width="20px"
                  height="20px"
                  color={colors.white}
                />
              </CircleButton>
            </CardBottom>
          </WhiteCard>
        </OptionPanel>
      </Content>
    </ModalShell>
  );
};

export default BillTemplateCreateTypeModal;

/* ----------------------------------
 * Styles
 ---------------------------------- */

const WarningBox = styled.div`
  width: 100%;
  background: ${colors.warning50};
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const WarningText = styled.div`
  font-family: ${fonts.label4Medium.fontFamily};
  font-size: ${fonts.label4Medium.fontSize};
  line-height: ${fonts.label4Medium.lineHeight};
  color: ${colors.warning100};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

/**
 * 핵심: blue50 배경이 "보이도록" white 카드(WhiteCard)를 blue50 패널(OptionPanel)로 감쌈
 * 두번째 이미지처럼 배경이 실제로 깔린다.
 */
const OptionPanel = styled.div<{ $disabled: boolean }>`
  width: 100%;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;

const WhiteCard = styled.div<{ $disabled: boolean }>`
  width: 100%;
  border-radius: 18px;
  background: ${({ $disabled }) =>
    $disabled ? colors.gray50 : colors.tableHover};
  border: 1px solid ${colors.gray100};
  overflow: hidden;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition:
    background 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;

  &:hover {
    background: ${({ $disabled }) =>
      $disabled ? colors.gray50 : colors.blue100};
    box-shadow: ${({ $disabled }) =>
      $disabled ? "none" : "0px 2px 4px 0px rgba(0, 0, 0, 0.15)"};
  }
`;

const PreviewFrame = styled.div`
  width: 100%;
  padding: 18px 18px 0 18px;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const CardBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px;
  background: ${colors.white};
`;

const TextArea = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.div`
  font-family: ${
    fonts.title3SemiBold?.fontFamily ?? fonts.body2SemiBold.fontFamily
  };
  font-size: ${fonts.title3SemiBold?.fontSize ?? fonts.body2SemiBold.fontSize};
  line-height: ${
    fonts.title3SemiBold?.lineHeight ?? fonts.body2SemiBold.lineHeight
  };
  color: ${colors.gray900};
`;

const CardSubTitle = styled.div`
  margin-top: 6px;
  font-family: ${fonts.body2Medium?.fontFamily ?? fonts.body3Medium.fontFamily};
  font-size: ${fonts.body2Medium?.fontSize ?? fonts.body3Medium.fontSize};
  line-height: ${fonts.body2Medium?.lineHeight ?? fonts.body3Medium.lineHeight};
  color: ${colors.gray600};
`;

const CircleButton = styled.button<{ $disabled: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  background: ${({ $disabled }) =>
    $disabled ? colors.gray200 : colors.blue600};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 1 : 0.92)};
  }
`;
